<script setup lang="ts">
import { computed, type PropType } from 'vue';

const props = defineProps({
    axis: {
        type: Number,
        required: true
    },
    /** Eixo da incisão (graus, 0-180) — 0 é tratado como "não informado". */
    incision: {
        type: Number,
        required: false
    },
    /** Magnitude do SIA (D) — 0 é tratado como "não informado". */
    sia: {
        type: Number,
        required: false
    },
    /** Qual olho é esse desenho — usado só pra espelhar o rótulo "Temporal"
     * (lado temporal é o lado de fora da cabeça: direita da imagem no OE,
     * esquerda da imagem no OD). Não afeta o cálculo do eixo. */
    eye: {
        type: String as PropType<'od' | 'os'>,
        required: false,
        default: 'od'
    }
})

const CENTER = 100
const RADIUS = 76
const DEG_TO_RAD = Math.PI / 180

// Mesmas cores de assets/colors/_colors.sass, só que como valor literal —
// esse SVG precisa se virar sozinho sem CSS externo (ver comentário grande
// mais abaixo sobre a extração pro PDF).
const COLOR_EYE_FILL = '#f2f2f2'      // colors.$secondary-contrast
const COLOR_PUPIL_FILL = '#e5e5e5'    // colors.$primary-contrast
const COLOR_BORDER = '#B5B1B1'        // colors.$border
const COLOR_AXIS = '#831717'          // colors.$error
const COLOR_INCISION = '#2B5799'      // colors.$info
const COLOR_TEXT = '#454545'          // colors.$secondary

// Convenção TABO: 0° à direita (3h), sentido anti-horário até 180° à
// esquerda (9h) — a mesma convenção usada pro eixo de K1/K2 no app inteiro,
// então o eixo calculado (result.axis) já vem pronto nessa escala.
function pointAt(deg: number, r: number = RADIUS) {
    return {
        x: CENTER + r * Math.cos(deg * DEG_TO_RAD),
        y: CENTER - r * Math.sin(deg * DEG_TO_RAD),
    }
}

function unitVector(deg: number) {
    const rad = deg * DEG_TO_RAD
    return { x: Math.cos(rad), y: -Math.sin(rad) }
}

// Ponta de seta desenhada como triângulo (polygon), calculada na mão em vez
// de usar <marker>/<defs> do SVG — o html2canvas (usado pra gerar o PDF) tem
// suporte capenga a <marker>, e isso estava quebrando a exportação inteira.
function arrowPolygon(deg: number, tipRadius: number, length = 7, halfWidth = 4): string {
    const dir = unitVector(deg)
    const perp = { x: -dir.y, y: dir.x }
    const tip = { x: CENTER + dir.x * tipRadius, y: CENTER + dir.y * tipRadius }
    const baseX = CENTER + dir.x * (tipRadius - length)
    const baseY = CENTER + dir.y * (tipRadius - length)
    const b1 = { x: baseX + perp.x * halfWidth, y: baseY + perp.y * halfWidth }
    const b2 = { x: baseX - perp.x * halfWidth, y: baseY - perp.y * halfWidth }
    return `${tip.x},${tip.y} ${b1.x},${b1.y} ${b2.x},${b2.y}`
}

// Antes era uma linha só passando pelo centro (ponta a ponta) — cruzava por
// cima do número do eixo e ficava ilegível. Agora são 2 segmentos separados,
// cada um saindo de fora do círculo central (INNER_R, depois do círculo do
// número) até a borda — mesma direção/seta, mas sem riscar o número.
const AXIS_INNER_R = 26

const axisArrows = computed(() => {
    return [props.axis, props.axis + 180].map((deg) => ({
        inner: pointAt(deg, AXIS_INNER_R),
        outer: pointAt(deg, RADIUS),
    }))
})

const arrowHeads = computed(() => ({
    start: arrowPolygon(props.axis, RADIUS),
    end: arrowPolygon(props.axis + 180, RADIUS),
}))

const ticks = computed(() => {
    const degs = [0, 45, 90, 135, 180, 225, 270, 315]
    return degs.map(deg => ({
        deg,
        inner: pointAt(deg, RADIUS - 8),
        outer: pointAt(deg, RADIUS),
        label: pointAt(deg, RADIUS + 14),
    }))
})

const showIncision = computed(() => {
    return props.incision != undefined && props.incision > 0
})

const incisionMark = computed(() => ({
    inner: pointAt(props.incision ?? 0, RADIUS - 4),
    outer: pointAt(props.incision ?? 0, RADIUS + 10),
}))

// Fixo em posição de tela (não em ângulo do círculo) pra não arriscar
// colidir com os ticks de grau nem estourar o viewBox — só espelha lado a
// lado (esquerda/direita) conforme o olho.
const temporalLabel = computed(() => {
    const isOd = props.eye !== 'os'
    return {
        x: isOd ? 34 : 166,
        anchor: isOd ? 'start' : 'end',
    }
})

// Mesma regra do showIncision: 0 é "não informado" (SIA ainda não é campo
// obrigatório), então só mostra a linha quando há valor de fato.
const siaValueText = computed(() => {
    if (props.sia == undefined || props.sia <= 0) return undefined
    const eixo = props.incision != undefined && props.incision > 0 ? `${props.incision.toFixed(0)}º` : '—'
    return `${props.sia.toFixed(2).replace('.', ',')}D @ ${eixo}`
})
</script>

<template>
    <div class="eye-axis">
        <!-- Fora do SVG (HTML normal) — dentro dele, o texto brigava por
             espaço com o desenho e encolhia o olho. Com legenda, pra não
             ficar um valor solto sem dizer do que se trata. -->
        <div class="eye-axis__caption" v-if="siaValueText">
            <p class="eye-axis__caption-row">
                <span class="eye-axis__caption-tag">SIA:</span>
                <span class="eye-axis__caption-value">{{ siaValueText }}</span>
            </p>
        </div>

        <!--
          Esse SVG é lido pelo html2canvas na exportação de PDF, que reconstrói
          cada <svg> como uma imagem isolada (data:image/svg+xml) — precisa do
          xmlns explícito (senão o navegador recusa carregar) e não enxerga o CSS
          do resto do site, então toda cor/traço aqui é atributo direto no
          elemento (fill/stroke), não classe. Regra: qualquer coisa que precise
          aparecer no PDF, bota como atributo, não como CSS.
        -->
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200" class="eye-axis-diagram" role="img" :aria-label="`Eixo recomendado: ${axis.toFixed(0)} graus`">
            <circle :cx="CENTER" :cy="CENTER" :r="RADIUS" :fill="COLOR_EYE_FILL" :stroke="COLOR_BORDER" stroke-width="1.5" />
            <circle :cx="CENTER" :cy="CENTER" r="20" :fill="COLOR_PUPIL_FILL" :stroke="COLOR_BORDER" stroke-width="1" />

            <g v-for="tick in ticks" :key="tick.deg">
                <line :x1="tick.inner.x" :y1="tick.inner.y" :x2="tick.outer.x" :y2="tick.outer.y" :stroke="COLOR_BORDER" stroke-width="1.5" />
                <text :x="tick.label.x" :y="tick.label.y" font-size="9" :fill="COLOR_TEXT" text-anchor="middle" dominant-baseline="middle">{{ tick.deg }}º</text>
            </g>

            <text :x="temporalLabel.x" y="14" font-size="9" font-weight="600" :fill="COLOR_TEXT" :text-anchor="temporalLabel.anchor">Temporal</text>

            <line
                v-for="(seg, index) in axisArrows" :key="index"
                :x1="seg.inner.x" :y1="seg.inner.y"
                :x2="seg.outer.x" :y2="seg.outer.y"
                :stroke="COLOR_AXIS" stroke-width="1.5" opacity="0.65"
            />
            <polygon :points="arrowHeads.start" :fill="COLOR_AXIS" opacity="0.65" />
            <polygon :points="arrowHeads.end" :fill="COLOR_AXIS" opacity="0.65" />

            <!-- Igual à calculadora do Kane: o número do eixo (o mesmo que orienta
                 a seta acima) escrito grande no centro do desenho. -->
            <text :x="CENTER" :y="CENTER" font-size="18" font-weight="700" :fill="COLOR_TEXT" text-anchor="middle" dominant-baseline="central">{{ axis.toFixed(0) }}</text>

            <line
                v-if="showIncision"
                :x1="incisionMark.inner.x" :y1="incisionMark.inner.y"
                :x2="incisionMark.outer.x" :y2="incisionMark.outer.y"
                :stroke="COLOR_INCISION" stroke-width="2.5" stroke-linecap="round"
            />
        </svg>
    </div>
</template>

<style scoped lang="sass">
@use "../../assets/colors/colors"
@use "../../assets/fonts/fonts"

// Só o dimensionamento/posicionamento fica em CSS — isso é lido a partir do
// DOM normal (fora do SVG extraído pro PDF), então CSS funciona sem problema
// aqui, mesmo dentro do <svg>.
.eye-axis
    display: flex
    align-items: center
    gap: 0.8em

    &__caption
        display: flex
        flex-direction: column
        gap: 0.3em
        flex-shrink: 0
        white-space: nowrap

    &__caption-row
        margin: 0

    &__caption-tag
        font-size: 0.85em
        color: colors.$secondary
        opacity: 0.7
        margin-right: 0.3em

    &__caption-value
        font-size: 1em
        color: colors.$secondary
        @include fonts.medium

.eye-axis-diagram
    width: 100%
    max-width: 170px
    height: auto
    display: block
    flex-shrink: 0

    @media print
        max-width: 130px
</style>
